// Szolgáltatás-hozzárendelés MÁSOLÁSA — a KÖZÖS modál (`components/ui/dialog`), nem külön beúszó panel.
//
// MIÉRT MODÁL ÉS NEM AZ ŰRLAP-PANEL: a másolás egyetlen kérdést tesz fel — „hová?". A teljes szerkesztő
// újranyitása a forrás-értékekkel azt sugallná, hogy mindent át kell nézni, holott épp az a lényeg, hogy
// NEM kell: az árazás, a komponens-fa és a számlatétel-mezők változatlanul mennek át. A modál ezért csak a
// CÉL-t kéri (hatókör · ügyfél · fiók · indulás), és egy összefoglalóban megmutatja, mi jön át.
//
// A MENTÉS a sima create-végponton megy (`POST /service-assignments`) a KÖZÖS body-építővel — nincs külön
// „clone" végpont, tehát a BFF minden fala (tulajdonos-státusz, idegen fiók, kártya-tulajdon, one_time→
// tracking kényszerítés, komplex Σ-ár) ugyanúgy érvényes, és nem tud elcsúszni a kézi felviteltől.
//
// A mentés ELŐTT ugyanaz az AKTÍV-ELLENŐRZÉS fut, mint a szerkesztőben: másoláskor a legvalószínűbb hiba épp
// az, hogy a cél-fiók már megkapta ugyanezt a szolgáltatást. Nem blokkol, csak figyelmeztet.
import {
  BriefcaseIcon,
  BuildingOffice2Icon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type ActiveAssignmentExisting,
  ServiceAssignmentActiveWarningDialog,
} from '@/components/service-assignments/service-assignment-active-warning-dialog';
import { AttentionBanner } from '@/components/shared/attention-banner';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { EntityMultiSelect } from '@/components/shared/entity-multiselect';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { UsageRefsSelect } from '@/components/service-assignments/usage-refs-select';
import { Segmented } from '@/components/ui/segmented';
import { serviceAssignmentKeys } from '@/features/service-assignments/api';
import {
  cloneDropsClientRefs,
  cloneUsageRefDefaults,
  serviceAssignmentCloneSummary,
  serviceAssignmentCloneValues,
} from '@/features/service-assignments/clone';
import {
  isUsageBasedForm,
  serviceAssignmentFormToCreateBody,
  usageRefKey,
  usageRefOptions,
  type ServiceScope,
} from '@/features/service-assignments/form';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';

/** A másolandó sor — a listából kapott minimum (a részleteket az id-ból töltjük). */
export interface ServiceAssignmentCloneSource {
  id: string;
  clientId: string;
  accountId: string | null;
  scope: string;
  serviceName: string | null;
}

export interface ServiceAssignmentCloneDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → zárva. A másolás forrása. */
  source: ServiceAssignmentCloneSource | null;
}

/** ISO date-only (YYYY-MM-DD) HELYI idő szerint — a `@db.Date` határ nem csúszhat UTC-re. */
function todayIso(now: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function ServiceAssignmentCloneDialog({
  open,
  onOpenChange,
  source,
}: ServiceAssignmentCloneDialogProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.toLowerCase().startsWith('en') ? ('en' as const) : ('hu' as const);

  // A forrás RÉSZLETEI (snapshot-override, ár-sorok, komponens-fa, felelősök) + a form-forrás refs.
  const detailKey = serviceAssignmentKeys.detail(source?.id ?? '__none__');
  const detailQ = api.serviceAssignment.useQuery(
    detailKey,
    { params: { id: source?.id ?? '' } },
    { queryKey: detailKey, enabled: open && source !== null },
  );
  const detail = detailQ.data?.status === 200 ? detailQ.data.body : undefined;

  const refsQ = api.serviceAssignmentRefs.useQuery(
    serviceAssignmentKeys.refs(lang),
    { query: { lang } },
    { queryKey: serviceAssignmentKeys.refs(lang), enabled: open },
  );
  const refs = refsQ.data?.status === 200 ? refsQ.data.body : undefined;

  // ── A CÉL (ez az egyetlen bemenet) ──
  const [scope, setScope] = useState<ServiceScope>('account');
  const [clientId, setClientId] = useState('');
  const [accountId, setAccountId] = useState('');
  /** ÜGYFÉL-hatókör: az érintett fiókok (`assignment-accounts` pivot) — a számlázás ebből összegez. */
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState(todayIso());

  // Nyitáskor a forrásból indulunk: AZONOS ügyfél, ÜRES fiók. A fiókot szándékosan nem töltjük elő — a
  // másolás célja épp egy MÁSIK tulajdonos, és az előtöltött forrás-fiók egy elnézett kattintással
  // ugyanoda hozna létre egy duplikátumot. Az ÉRINTETT FIÓKOK viszont a FORRÁS kijelöléséből indulnak
  // (azonos ügyfél → a szerződés ugyanarra a hotel-körre szól).
  useEffect(() => {
    if (!open || !source) return;
    setScope(source.scope === 'client' ? 'client' : 'account');
    setClientId(source.clientId);
    setAccountId('');
    setStartDate(todayIso());
  }, [open, source]);

  const accountsForClient = useMemo(
    () => (refs?.accounts ?? []).filter((a) => a.clientId === clientId),
    [refs, clientId],
  );

  // Az ÉRINTETT FIÓKOK kezdőértéke. AZONOS ügyfélnél a forrás kijelölése (a detail `accountIds`-e), MÁS
  // ügyfélnél az ÚJ ügyfél ÖSSZES fiókja — ugyanaz a két szabály, mint a szerkesztőben (a legacy
  // alapértelmezése). Az őr egy ref, mert a `detail` és a `refs` KÜLÖN érkezik: a hatás az első olyan
  // körben fut, amikor már mindkettő megvan, és utána CSAK valódi ügyfél-váltásra.
  const elozoUgyfel = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !detail || !refs) {
      if (!open) elozoUgyfel.current = null;
      return;
    }
    if (elozoUgyfel.current === clientId) return;
    const valtott = elozoUgyfel.current !== null;
    elozoUgyfel.current = clientId;
    if (valtott) setAccountId('');
    setAccountIds(
      clientId === detail.clientId
        ? [...detail.accountIds]
        : refs.accounts.filter((a) => a.clientId === clientId).map((a) => a.id),
    );
  }, [open, detail, refs, clientId]);

  // A CÉL tulajdonos státusza — a másolt sor státuszát ez szűkíti (ugyanaz a szabály, mint a create-formon).
  const ownerStatus: string | null = useMemo(() => {
    if (!refs) return null;
    if (scope === 'account') {
      return accountId === ''
        ? null
        : (refs.accounts.find((a) => a.id === accountId)?.status ?? null);
    }
    return clientId === '' ? null : (refs.clients.find((c) => c.id === clientId)?.status ?? null);
  }, [refs, scope, clientId, accountId]);

  const summary = useMemo(() => {
    if (!detail) return null;
    const catalogName = refs?.plans.find((p) => p.id === detail.servicePlanId)?.name ?? null;
    return serviceAssignmentCloneSummary(detail, catalogName);
  }, [detail, refs]);

  const dropsRefs = detail !== undefined && cloneDropsClientRefs(detail, clientId);

  // A MÉRT AZONOSÍTÓK a célon — használat-alapú forrásnál kötelező választás (a cél-fiók más azonosítókat
  // kínál, mint a forrásé). A kezdőérték a forrás kötéséből az, ami a célon is választható.
  const usageBased =
    detail !== undefined &&
    isUsageBasedForm({
      pricing: { pricingType: detail.pricingType ?? detail.catalogPricingType ?? '' },
      usageSourceType: detail.usageSourceType ?? '',
      components: detail.children,
    });
  const refOptions = useMemo(
    () =>
      usageBased
        ? usageRefOptions({
            scope,
            accountId,
            accountIds,
            usageSourceType: detail?.usageSourceType ?? '',
            accounts: refs?.accounts ?? [],
          })
        : [],
    [usageBased, scope, accountId, accountIds, detail, refs],
  );
  const [usageRefs, setUsageRefs] = useState<string[]>([]);
  const refOptionKeys = refOptions.map((o) => o.key).join(',');
  // A kézi választás MEGMARAD (csak a már nem választható kulcs esik ki); a forrás kötéséből számolt alapérték
  // csak akkor áll be, ha még nincs érvényes választás — különben egy cél-fiók hozzáadása felülírná, amit a
  // felhasználó épp kijelölt.
  useEffect(() => {
    if (!detail) return;
    const valid = refOptionKeys === '' ? [] : refOptionKeys.split(',');
    setUsageRefs((prev) => {
      const kept = prev.filter((k) => valid.includes(k));
      return kept.length > 0
        ? kept
        : cloneUsageRefDefaults((detail.usageRefs ?? []).map(usageRefKey), valid);
    });
  }, [detail, refOptionKeys]);

  const mutation = useEntityMutation<void>({
    mutationFn: () => {
      // A `buildValues` csak akkor hívódik, ha a gomb aktív (detail + érvényes cél) — l. `canSubmit`.
      const serviceId = refs?.plans.find((p) => p.id === detail!.servicePlanId)?.serviceId ?? '';
      const values = serviceAssignmentCloneValues(
        detail!,
        { scope, clientId, accountId, accountIds, startDate, ownerStatus, usageRefs },
        serviceId,
        (planId) => refs?.plans.find((p) => p.id === planId)?.serviceId ?? '',
      );
      return api.serviceAssignmentCreate.mutation({
        body: serviceAssignmentFormToCreateBody(values),
      });
    },
    invalidateKeys: [serviceAssignmentKeys.lists()],
    messages: {
      success: t('toast:serviceAssignments.cloned'),
      conflict: t('toast:serviceAssignments.conflict'),
      forbidden: t('toast:serviceAssignments.forbidden'),
      validation: t('toast:serviceAssignments.validation'),
      error: t('toast:serviceAssignments.error'),
    },
  });

  // ── Aktív-hozzárendelés figyelmeztetés (nem blokkoló) ──
  const [warnOpen, setWarnOpen] = useState(false);
  const [warnExisting, setWarnExisting] = useState<ActiveAssignmentExisting | null>(null);
  const [checking, setChecking] = useState(false);

  const doSubmit = (): void => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        setWarnOpen(false);
        onOpenChange(false);
      },
    });
  };

  const onConfirm = async (): Promise<void> => {
    if (!detail) return;
    const plan = refs?.plans.find((p) => p.id === detail.servicePlanId);
    // one_time konstrukcióból bármennyi lehet → a BFF is kihagyja az ellenőrzést.
    if (plan?.billingType !== 'one_time') {
      const componentPlanIds = detail.children.map((c) => c.servicePlanId);
      try {
        setChecking(true);
        const res = await api.serviceAssignmentActiveCheck.query({
          query: {
            servicePlanId: detail.servicePlanId,
            clientId,
            accountId: scope === 'account' ? accountId : undefined,
            scope,
            ...(componentPlanIds.length > 0 ? { componentServicePlanIds: componentPlanIds } : {}),
          },
        });
        if (res.status === 200 && res.body.active) {
          setWarnExisting(res.body.existing);
          setWarnOpen(true);
          return;
        }
      } catch {
        // az ellenőrzés hibája NEM blokkolja a másolást (best-effort figyelmeztetés)
      } finally {
        setChecking(false);
      }
    }
    doSubmit();
  };

  const busy = mutation.isPending || checking;
  const canSubmit =
    detail !== undefined &&
    clientId !== '' &&
    // fiók-hatókör → cél-fiók; ügyfél-hatókör → legalább egy ÉRINTETT fiók (az üres kijelölés a generálóban
    // „az ügyfél összes fiókja" jelentésű fallback, tehát csendben rosszul számlázna — l. a szerkesztő falát).
    (scope === 'client' ? accountIds.length > 0 : accountId !== '') &&
    // használat-alapú tételnél a mért azonosító kötelező (a BFF is 422-zi)
    (!usageBased || usageRefs.length > 0) &&
    startDate !== '' &&
    !busy;

  const clientOptions = (refs?.clients ?? []).map((c) => ({ id: c.id, label: c.name }));
  const accountOptions = accountsForClient.map((a) => ({ id: a.id, label: a.name }));
  const accountMultiOptions = accountsForClient.map((a) => ({ id: a.id, name: a.name }));

  const summaryRow = (label: string, value: string): JSX.Element => (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('serviceAssignments.clone.title')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-5">
            <DialogDescription>
              {t('serviceAssignments.clone.desc', { name: source?.serviceName ?? '' })}
            </DialogDescription>

            {/* MI JÖN ÁT — a felhasználó mentés ELŐTT lássa, hogy az árazás és a komponensek is másolódnak. */}
            {summary && (
              <dl className="border-border bg-muted/30 space-y-1.5 rounded-lg border p-3 text-sm">
                {summaryRow(t('serviceAssignments.clone.summaryPlan'), summary.planName ?? '—')}
                {summaryRow(
                  t('serviceAssignments.clone.summaryPricing'),
                  summary.pricingType
                    ? t(`serviceAssignments.pricingTypeLabel.${summary.pricingType}`, {
                        defaultValue: summary.pricingType,
                      })
                    : '—',
                )}
                {summaryRow(
                  t('serviceAssignments.clone.summaryPrices'),
                  t('serviceAssignments.clone.priceCount', { count: summary.priceCount }),
                )}
                {summary.componentCount > 0 &&
                  summaryRow(
                    t('serviceAssignments.clone.summaryComponents'),
                    t('serviceAssignments.clone.componentCount', {
                      count: summary.componentCount,
                    }),
                  )}
                {summary.hasPeople &&
                  summaryRow(
                    t('serviceAssignments.clone.summaryPeople'),
                    dropsRefs
                      ? t('serviceAssignments.clone.peopleExpertOnly')
                      : t('serviceAssignments.clone.peopleAll'),
                  )}
              </dl>
            )}

            {/* ÜGYFÉL-VÁLTÁS: az ügyfélhez kötött hivatkozások nem jöhetnek át — ezt KI KELL MONDANI. */}
            {dropsRefs && (
              <AttentionBanner
                tone="warning"
                title={t('serviceAssignments.clone.otherClientWarning')}
              />
            )}

            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t('serviceAssignments.form.scope')}</span>
              <Segmented
                value={scope}
                onChange={(v) => setScope(v as ServiceScope)}
                aria-label={t('serviceAssignments.form.scope')}
                disabled={busy}
                options={[
                  {
                    value: 'client',
                    label: t('serviceAssignments.scope.client'),
                    icon: <BriefcaseIcon className="size-3.5" />,
                  },
                  {
                    value: 'account',
                    label: t('serviceAssignments.scope.account'),
                    icon: <BuildingOffice2Icon className="size-3.5" />,
                  },
                ]}
              />
            </div>

            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t('serviceAssignments.form.client')}</span>
              <EntityCombobox
                value={clientId === '' ? null : clientId}
                onChange={(id) => setClientId(id ?? '')}
                options={clientOptions}
                initialOption={clientOptions.find((o) => o.id === clientId) ?? null}
                placeholder={t('serviceAssignments.form.clientPlaceholder')}
                aria-label={t('serviceAssignments.form.client')}
                disabled={busy}
              />
            </div>

            {scope === 'account' ? (
              <div className="space-y-1.5">
                <span className="text-sm font-medium">{t('serviceAssignments.form.account')}</span>
                <EntityCombobox
                  value={accountId === '' ? null : accountId}
                  onChange={(id) => setAccountId(id ?? '')}
                  options={accountOptions}
                  initialOption={accountOptions.find((o) => o.id === accountId) ?? null}
                  placeholder={
                    clientId === ''
                      ? t('serviceAssignments.form.accountPickClient')
                      : t('serviceAssignments.form.accountPlaceholder')
                  }
                  aria-label={t('serviceAssignments.form.account')}
                  disabled={busy || clientId === ''}
                />
              </div>
            ) : (
              // ÜGYFÉL-HATÓKÖR: az érintett fiókok — a másolat ugyanúgy nem létezhet kijelölés nélkül, mint
              // a kézzel felvett sor (a számlázás ebből a körből összegez).
              <div className="space-y-1.5">
                <span className="text-sm font-medium">
                  {t('serviceAssignments.form.accountIds')}
                </span>
                <EntityMultiSelect
                  options={accountMultiOptions}
                  value={accountIds}
                  onChange={setAccountIds}
                  placeholder={
                    clientId === ''
                      ? t('serviceAssignments.form.accountPickClient')
                      : t('serviceAssignments.form.accountIdsPlaceholder')
                  }
                  countLabel={(count) => t('serviceAssignments.form.accountIdsCount', { count })}
                  aria-label={t('serviceAssignments.form.accountIds')}
                />
                <p className="text-muted-foreground text-xs">
                  {t('serviceAssignments.form.accountIdsHint')}
                </p>
              </div>
            )}

            {/* MÉRT AZONOSÍTÓK: a cél-fiók más azonosítókat kínál, mint a forrásé — használat-alapú tételnél
                ezért itt is választani kell (a forrás kötéséből az marad, ami a célon is érvényes). */}
            {usageBased && (
              <UsageRefsSelect
                options={refOptions}
                value={usageRefs}
                onChange={setUsageRefs}
                showAccount={scope === 'client'}
                disabled={busy}
              />
            )}

            {/* INDULÁS: a forrásé a FORRÁS szerződéséé — egy régi dátummal a másolat visszamenőleg
                lenne esedékes. Alapérték a mai nap; a zárás-dátum sosem öröklődik. */}
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t('serviceAssignments.clone.startDate')}</span>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                label={t('serviceAssignments.clone.startDate')}
                language={i18n.language}
                disabled={busy}
              />
              <p className="text-muted-foreground text-xs">
                {t('serviceAssignments.clone.startDateHint')}
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              {t('serviceAssignments.cancel')}
            </Button>
            <Button
              type="button"
              variant="brand"
              onClick={() => void onConfirm()}
              disabled={!canSubmit}
            >
              <DocumentDuplicateIcon className="size-4" />
              {busy ? t('serviceAssignments.clone.saving') : t('serviceAssignments.clone.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ServiceAssignmentActiveWarningDialog
        open={warnOpen}
        onOpenChange={(o) => {
          if (!o) setWarnOpen(false);
        }}
        existing={warnExisting}
        pending={mutation.isPending}
        onProceed={doSubmit}
        onViewExisting={() => {
          setWarnOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
